import { useEffect, useRef, useState } from 'react';
import { UserX } from 'lucide-react';
import { EditPencil } from 'iconoir-react';
import { v4 as uuidv4 } from 'uuid';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { Dialog } from 'components/ui/dialog';
import { EditProfile } from '../modals/edit-profile/edit-profile';
import DummyProfile from 'assets/images/dummy_profile.png';
import { UpdatePassword } from '../modals/update-password/update-password';
import { Skeleton } from 'components/ui/skeleton';
import { useGetAccount } from '../../hooks/use-account';
import { AuthenticatorAppSetup } from '../modals/authenticator-app-setup/authenticator-app-setup';
import { EmailVerification } from '../modals/email-verification/email-verification';
import { MfaMethodsCard } from './mfa-methods-card';
import { MfaDialogState } from '../../enums/mfa-dialog-state.enum';
import { UserMfaType } from '../../enums/user-mfa-type-enum';
import { useTranslation } from 'react-i18next';
import { User } from "/types/user-type";
import { useCommonStore } from "state/store/common/useCommonStore";
import { DeactivateAccount } from '../modals/deactivate-account/deactivate-account';
import { useOrganizations } from 'features/organization/hooks/use-organization';
import { PersonalCard } from '../personal-card/personal-card';

/**
 * `GeneralInfo` component renders a user profile and account security section with options to
 * edit the profile, manage two-factor authentication (MFA), and change the password.
 * It also handles various dialogs for MFA setup, authentication app setup, and email verification.
 *
 * @returns {JSX.Element} The rendered user profile and account security sections.
 */

export const GeneralInfo = () => {
  const { data, isLoading } = useGetAccount({
    enabled: true
  });
  const setUser = useCommonStore((state) => (state.setUser));
  const isOrgAdmin = useCommonStore((state) => (state.isOrgAdmin));
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [currentDialog, setCurrentDialog] = useState<MfaDialogState>(MfaDialogState.NONE);
  const isLocalStorageSet = useRef(false);
  const { t } = useTranslation();
  const closeAllModals = () => setCurrentDialog(MfaDialogState.NONE);
  const [userInfo, setUserInfo] = useState<User>();
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);

  // Fetch organizations for org admin users
  const { data: organizationsData, isLoading: isOrganizationsLoading } = useOrganizations({
    page: 0,
    pageSize: 1,
    enabled: isOrgAdmin && !!userInfo // Only fetch if user is org admin and userInfo is available
  });

  const handleEditProfileClose = () => {
    setIsEditProfileModalOpen(false);
  };

  const [joinedDate, setJoinedDate] = useState<Date | null>(null);
  const [lastLoggedInDate, setLastLoggedInDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!userInfo || isLocalStorageSet.current) return;

    setJoinedDate(new Date(userInfo.createdDate));
    setLastLoggedInDate(new Date(userInfo.lastLoggedInTime));

    const shouldSetMfaState =
      userInfo.userMfaType === UserMfaType.NONE ||
      (userInfo.userMfaType !== UserMfaType.NONE && userInfo.mfaEnabled);

    if (shouldSetMfaState) {
      localStorage.setItem('initialMfaUserState', String(userInfo.mfaEnabled));
      isLocalStorageSet.current = true;
    }
  }, [userInfo]);

  useEffect(() => {
    if (data) {
      setUserInfo(data);
      setUser(data)
    }
  }, [data, setUserInfo, setUser]);

  return (
    <div className="flex flex-col gap-6">
      <Card className="w-full border border-border rounded-lg shadow-sm bg-card">
        <CardHeader className="p-0 hidden">
          <CardTitle />
          <CardDescription />
        </CardHeader>
        <CardContent className="flex flex-col gap-5 p-4 sm:p-6">
          <div className="flex justify-between">
            <div className="flex items-center">
              <div className="relative w-16 h-16 rounded-full border overflow-hidden border-white shadow-sm">
                {isLoading ? (
                  <Skeleton className="w-16 h-16 rounded-full" />
                ) : (
                  <img
                    src={
                      userInfo?.profileImageUrl !== ''
                        ? (userInfo?.profileImageUrl ?? DummyProfile)
                        : DummyProfile
                    }
                    alt="Profile"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex flex-col gap-1 ml-3 sm:ml-9">
                {isLoading ? (
                  <>
                    <Skeleton className="w-40 h-5" />
                    <Skeleton className="w-48 h-4 mt-1" />
                  </>
                ) : (
                  <>
                    <h1 className="text-xl text-high-emphasis font-semibold">
                      {userInfo?.firstName} {userInfo?.lastName}
                    </h1>
                    <p className="text-sm text-medium-emphasis">{userInfo?.email}</p>
                  </>
                )}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setIsEditProfileModalOpen(true)}>
              <EditPencil width={12} height={12} strokeWidth={1.75} className="" />
              <span className="text-sm font-bold sr-only sm:not-sr-only sm:whitespace-nowrap">
                {t('EDIT')}
              </span>
            </Button>
            <Dialog open={isEditProfileModalOpen} onOpenChange={setIsEditProfileModalOpen}>
              {userInfo && <EditProfile userInfo={userInfo} onClose={handleEditProfileClose} />}
            </Dialog>
          </div>
          <Separator orientation="horizontal" />
          <div className={`grid gap-6 ${isOrgAdmin ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3'}`}>
            {isLoading ? (
              Array.from({ length: isOrgAdmin ? 4 : 3 }).map(() => (
                <div key={uuidv4()}>
                  <Skeleton className="h-3 w-20 mb-1" />
                  <Skeleton className="h-5 w-36" />
                </div>
              ))
            ) : (
              <>
                <div>
                  <p className="text-medium-emphasis text-xs font-normal">{t('MOBILE_NO')}</p>
                  <p className="text-high-emphasis text-sm">{userInfo?.phoneNumber === "" || !userInfo?.phoneNumber ? "-" : userInfo.phoneNumber}</p>
                </div>
                <div>
                  <p className="text-medium-emphasis text-xs font-normal">{t('JOINED_ON')}</p>
                  <p className="text-high-emphasis text-sm">
                    {joinedDate
                      ? `${joinedDate.toLocaleDateString('de-CH', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}, ${joinedDate.toLocaleTimeString('de-CH', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}`
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-medium-emphasis text-xs font-normal">{t('LAST_LOGGED_IN')}</p>
                  <p className="text-high-emphasis text-sm">
                    {lastLoggedInDate
                      ? `${lastLoggedInDate.toLocaleDateString('de-CH', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}, ${lastLoggedInDate.toLocaleTimeString('de-CH', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}`
                      : '-'}
                  </p>
                </div>
                {isOrgAdmin && (
                  <div>
                    <p className="text-medium-emphasis text-xs font-normal">{t('ORGANIZATION')}</p>
                    <p className="text-high-emphasis text-sm">
                      {isOrganizationsLoading ? (
                        <Skeleton className="h-5 w-32" />
                      ) : (
                        organizationsData?.data?.[0]?.name || '-'
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
      <MfaMethodsCard
        userInfo={userInfo}
        isLoading={isLoading}
        onSelectMethod={setCurrentDialog}
      />
      {currentDialog === MfaDialogState.AUTHENTICATOR_APP_SETUP && (
        <AuthenticatorAppSetup
          userInfo={userInfo}
          onClose={closeAllModals}
        />
      )}
      {currentDialog === MfaDialogState.EMAIL_VERIFICATION && (
        <EmailVerification
          userInfo={userInfo}
          onClose={closeAllModals}
        />
      )}
      {/* Shown on /profile and in the settings dialog's Account tab, which both render GeneralInfo;
          above the password and delete-account cards. */}
      <PersonalCard account={userInfo} />
      <Card className="w-full border border-border rounded-lg shadow-sm bg-card">
        <CardContent className="flex flex-col gap-6 p-4 sm:p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl text-high-emphasis font-semibold">{t('CHANGE_PASSWORD')}</h1>
            <p className="text-sm text-medium-emphasis">
              {t('UPDATE_PASSWORD_KEEP_ACCOUNT_SAFE')}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-md text-sm font-medium shrink-0"
            onClick={() => setIsChangePasswordModalOpen(true)}
          >
            {t('UPDATE_PASSWORD')}
          </Button>
          <UpdatePassword
            onClose={() => setIsChangePasswordModalOpen(false)}
            open={isChangePasswordModalOpen}
            onOpenChange={setIsChangePasswordModalOpen}
          />
        </CardContent>
      </Card>
      <Card className="w-full border border-border rounded-lg shadow-sm bg-card">
        <CardContent className="flex flex-col gap-6 p-4 sm:p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl text-high-emphasis font-semibold">{t('DELETE_ACCOUNT')}</h1>
            <p className="text-sm text-medium-emphasis">
              {t('DELETE_ACCOUNT_MESSAGE')}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-md text-destructive hover:text-destructive text-sm font-medium shrink-0"
            onClick={() => setIsDeactivateModalOpen(true)}
          >
            <UserX className="w-4 h-4" />
            {t('DELETE_ACCOUNT')}
          </Button>
          <Dialog open={isDeactivateModalOpen} onOpenChange={setIsDeactivateModalOpen}>
            <DeactivateAccount onClose={() => setIsDeactivateModalOpen(false)} />
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
};
